"use client";

import { useMemo, useState } from "react";
import { format, subDays, eachDayOfInterval, isSameDay } from "date-fns";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card_v3";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select_v3";
import { cn } from "lib/utils";
import localization from "services/localization";

export default function UsageOverTimeChart({ messages = [], users = [], theme,dateRange,selectedUser }) {
  // const [chartPeriod, setChartPeriod] = useState("30");
  const isSingleUser = selectedUser && selectedUser !== "all";
  const selectedUserName =
  users.find((u) => u.id === selectedUser)?.name || "";

  const card_bg =
    theme === "dark"
      ? "bg-[var(--card-bg)] border-border"
      : "bg-card border-border";

  const select_bg =
    theme === "dark"
      ? "bg-[var(--card-bg)] text-foreground border-border"
      : "bg-card text-foreground border-border";

const usersMap = useMemo(() => {
  const map = {};
  users.forEach((u) => {
    map[u.id] = u;
  });
  return map;
}, [users]);

const chartData = useMemo(() => {
  if (!Array.isArray(messages) || !dateRange?.from || !dateRange?.to) return [];
  const start = new Date(dateRange.from);
  const end = new Date(dateRange.to);

  const grouped = {};

messages.forEach((m) => {
  const key = format(new Date(m.date), "yyyy-MM-dd");

  if (!grouped[key]) {
    grouped[key] = [];
  }

  grouped[key].push(m);
});

  const interval = eachDayOfInterval({ start, end });

  return interval.map((day) => {
    const dayMessages = grouped[format(day, "yyyy-MM-dd")] || [];

     // 👉 CASE: SINGLE USER
    if (isSingleUser) {
     const total = dayMessages
  .filter((m) => m.user_id === selectedUser)
  .reduce(
    (sum, m) => sum + (m.metrics?.total_messages || 0),
    0
  );

      return {
        date: format(day, "MMM dd"),
        Total: total,
      };
    }

    // 👉 CASE: ALL USERS (ROLE SPLIT)
    let teachers = 0;
    let students = 0;
    let admin = 0;

    dayMessages.forEach((m) => {
      const user = usersMap[m.user_id];

      const count = m.metrics?.total_messages || 0;
      if (user?.role === "teacher") {
        teachers += count;
      } else if (user?.role === "student") {
        students += count;
      } else if (user?.role === "administrative") {
        admin += count;
      }
    });

    return {
      date: format(day, "MMM dd"),
      Teachers: teachers,
      Students: students,
      Admin: admin,
      Total: teachers + students + admin,
    };
  });
}, [messages, users, dateRange,isSingleUser]);


  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
const total = payload.reduce((sum, p) => sum + p.value, 0);
    return (
      <div
        className={cn(
          "rounded-lg border p-3 shadow-lg",
          theme === "dark"
            ? "bg-[#1a2332] border-border text-foreground"
            : "bg-card border-border text-foreground"
        )}
      >
        <p className="mb-2 text-sm font-medium">{label}</p>
       {payload.map((entry, i) => (
  <div key={i} className="flex items-center justify-between gap-2 text-xs">
    <div className="flex items-center gap-2">
      <span
        className="h-2.5 w-2.5 rounded-full"
        style={{ backgroundColor: entry.color }}
      />
      <span className="text-muted-foreground">{entry.name}</span>
    </div>

    <span className="font-medium tabular-nums">
      {entry.value}
    </span>
  </div>
))}
         {/* Divider (optional but nice) */}
      <div className="my-2 h-px bg-border" />
{!isSingleUser && (
   <div className="flex justify-between text-xs font-semibold">
        <span>Total</span>
        <span>{total}</span>
      </div>
)}
     
      </div>
    );
  };

  if (!chartData.length) {
  return (
    <Card className={cn(card_bg)}>
      <CardContent className="flex h-[300px] items-center justify-center text-sm text-muted-foreground">
        {localization.no_data || "No data"}
      </CardContent>
    </Card>
  );
}

  return (
    <Card className={cn("col-span-full", card_bg)}>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-base font-semibold text-foreground">
  {isSingleUser
    ? `${selectedUserName} Activity Over Time`
    : localization.daily_usage || "Daily Usage"}
</CardTitle>

        {/* <Select value={chartPeriod} onValueChange={setChartPeriod}>
          <SelectTrigger className={cn("w-[140px] h-8 text-xs", select_bg)}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">
              {localization.last_7_days || "Last 7 days"}
            </SelectItem>
            <SelectItem value="15">
              {localization.last_15_days || "Last 15 days"}
            </SelectItem>
            <SelectItem value="30">
              {localization.last_30_days || "Last 30 days"}
            </SelectItem>
          </SelectContent>
        </Select> */}
      </CardHeader>

      <CardContent className="pt-2">
        <div className="h-[320px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={theme === "dark" ? "#1f2937" : "#e5e7eb"}
                vertical={false}
              />

              <XAxis
                dataKey="date"
                tick={{
                  fontSize: 11,
                  fill: theme === "dark" ? "#9ca3af" : "#6b7280",
                }}
                axisLine={false}
                tickLine={false}
              />

              <YAxis
                tick={{
                  fontSize: 11,
                  fill: theme === "dark" ? "#9ca3af" : "#6b7280",
                }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
              />

              <Tooltip content={<CustomTooltip />} />
{!isSingleUser && <Legend />}
            {isSingleUser ? (
  <Bar dataKey="Total" fill="#3b82f6" />
) : (
  <>
    <Bar dataKey="Teachers" stackId="a" fill="#3b82f6" />
    <Bar dataKey="Students" stackId="a" fill="#10b981" />
    <Bar dataKey="Admin" stackId="a" fill="#f59e0b" />
  </>
)}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}